// [Codex 2026-07-03] Local auth-bypass builds fetch real entitlements for E2E.
import { buildAuthJsonHeaders } from "./auth-headers";
import { getRuntimeApiBase } from "./runtime-api-base";

export type EntitlementKey =
  | "desktop.access"
  | "performance.access"
  | "briefs.mdb"
  | "briefs.full"
  | "riskflow.bookmarks"
  | "narrative.sessions"
  | "memorandums"
  | "scouts"
  | "station.t1"
  | "desk.plan"
  | "boardroom.access";

export type UserTier = "free" | "fintheon" | "fintheon_plus" | "fintheon_pro";
export type BillingCheckoutPlan =
  "analyst" | "one_year_license" | "desk_annual";

export interface PromotionReason {
  code: string;
  label: string;
  source: "domain" | "program" | "reward" | "stripe" | "offer";
  hidden?: boolean;
}

export interface StripeBenefit {
  code: string;
  label: string;
  trialDays?: number;
  couponId?: string | null;
  creditMonths?: number;
  royaltyRateBps?: number;
  hidden?: boolean;
}

export interface HiddenReward {
  code: string;
  label: string;
  earned: boolean;
  creditMonths?: number;
  expiresAt?: string | null;
  hidden: true;
}

export interface BillingOffer {
  plan: BillingCheckoutPlan;
  name: string;
  price: string;
  description: string;
  checkoutAvailable: boolean;
  unitAmountCents?: number;
  currency?: string;
}

export interface BillingStatusResponse {
  plan: "free" | "analyst" | "desk" | "boardroom";
  tier: UserTier;
  billingStatus: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  entitlements: Record<EntitlementKey, boolean>;
  policyReasons: PromotionReason[];
  stripeBenefits: StripeBenefit[];
  hiddenRewards: HiddenReward[];
  publicOffers: BillingOffer[];
  pendingCreditMonths: number;
}

export const defaultEntitlements: Record<EntitlementKey, boolean> = {
  "desktop.access": false,
  "performance.access": false,
  "briefs.mdb": true,
  "briefs.full": false,
  "riskflow.bookmarks": false,
  "narrative.sessions": false,
  memorandums: false,
  scouts: false,
  "station.t1": false,
  "desk.plan": false,
  "boardroom.access": false,
};

export const defaultBillingStatus: BillingStatusResponse = {
  plan: "free",
  tier: "free",
  billingStatus: "none",
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  trialEnd: null,
  currentPeriodEnd: null,
  entitlements: defaultEntitlements,
  policyReasons: [],
  stripeBenefits: [],
  hiddenRewards: [],
  publicOffers: [],
  pendingCreditMonths: 0,
};

export async function fetchBillingStatus(
  token?: string | null,
): Promise<BillingStatusResponse> {
  const allowUnauthedBillingStatus =
    import.meta.env.VITE_BYPASS_AUTH === "true";
  if (!token && !allowUnauthedBillingStatus) return defaultBillingStatus;
  const response = await fetch(`${getRuntimeApiBase()}/api/billing/status`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return readBillingResponse(response);
}

export async function startAnalystCheckout(): Promise<{ url: string }> {
  return startBillingCheckout("analyst");
}

export async function startBillingCheckout(
  plan: BillingCheckoutPlan,
  options: { deskId?: string; featureRequest?: string } = {},
): Promise<{ url: string }> {
  const response = await fetch(`${getRuntimeApiBase()}/api/billing/checkout`, {
    method: "POST",
    headers: await buildAuthJsonHeaders(),
    body: JSON.stringify({ plan, ...options }),
  });
  return readBillingResponse(response);
}

export async function openBillingPortal(): Promise<{ url: string }> {
  const response = await fetch(`${getRuntimeApiBase()}/api/billing/portal`, {
    method: "POST",
    headers: await buildAuthJsonHeaders(),
  });
  return readBillingResponse(response);
}

export async function reconcileBillingRewards(): Promise<{
  ok: boolean;
  appliedCreditMonths: number;
  stripeBalanceTransactionId?: string;
}> {
  const response = await fetch(
    `${getRuntimeApiBase()}/api/billing/rewards/reconcile`,
    {
      method: "POST",
      headers: await buildAuthJsonHeaders(),
    },
  );
  return readBillingResponse(response);
}

async function readBillingResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => ({}));
  if (response.ok) return payload as T;
  const message =
    typeof payload.message === "string"
      ? payload.message
      : typeof payload.error === "string"
        ? payload.error
        : "Billing request failed";
  throw new Error(message);
}
